import { getLogger } from "@goodgamestudios/logger";

import { getQualifiedClassName } from "@goodgamestudios/createjs-utilities";
import { URLLoaderService } from "@goodgamestudios/createjs-utilities";
import { URLRequest } from "@goodgamestudios/createjs-utilities";

import { ExternalLoggingConstants } from "./ExternalLoggingConstants";

import { ILODataFacade } from "./facade/ILODataFacade";
import { LODataFacade } from "./facade/LODataFacade";
import { BasicLogObjectFactory } from "./factories/BasicLogObjectFactory";
import { AbstractLogObject } from "./logObjects/AbstractLogObject";

import Event = createjs.Event;

const logger = getLogger("ExternalLog");
const MD5 = require("md5.js");

/**
 * @author dkoehler
 *
 * logs various errors to logging server
 */
export class ExternalLog {

    public static MESSAGE_SINGLETON: string = "Calling constructor not allowed! logger instead.";
    public static MESSAGE_STATUS_CHANGE: string = "Status: '{0}'";
    public static MESSAGE_NO_ESSENTIALS: string = "You have to execute createEssentials in your '{0}' class.";
    public static MESSAGE_NOT_ACTIVE: string = "ExternalLog is not active, but wants to log '{0}' event";
    public static MESSAGE_SKIP: string = "Event '{0}' with subErrorID '{1}' was already sent. Skipping.";
    public static MESSAGE_SEND: string = "Sending event '{0}' with subErrorID '{1}' and payload '{2}'";

    public static EXCLUDED_PARAMS_FROM_HASH: string[] = [ExternalLoggingConstants.PARAM_UTC_TIME_STAMP, ExternalLoggingConstants.PARAM_SESSION_LENGTH];
    public static DISPATCHED_HASHES_MAX_LENGTH: number = 25;

    private static _logInstance: ExternalLog;

    private _dispatchedHashes: string[];
    private _isActive: boolean = true;
    private _facade: ILODataFacade = null;

    constructor() {
        this.init();

        if (ExternalLog._logInstance) {
            throw new Error(ExternalLog.MESSAGE_SINGLETON);
        }
    }

    public static get logger(): ExternalLog {
        if (!ExternalLog._logInstance) {
            ExternalLog._logInstance = new ExternalLog();
        }
        return ExternalLog._logInstance;
    }

    public dispose(): void {
        this._dispatchedHashes = [];
    }

    /**
     * log message to server
     *
     * @param loFactory, the typed factory
     * @param force If True event will skip check-hash-phase phase and will be sent right away. If False
     * event payload will be hashed(without timestamp and session length), stored and sent, when an event
     * arrives with the same payload its hash will be compared to hash pool and if there is a match the
     * event won't be sent again.
     *
     */
    public log(loFactory: BasicLogObjectFactory, force: boolean = false): void {
        if (!this.isActive) {
            logger.warn(this.parseMessage(ExternalLog.MESSAGE_NOT_ACTIVE, loFactory));
            return;
        }

        loFactory.dataFacade = this._facade;

        let lo: AbstractLogObject = loFactory.create();
        let hash: string = this.takeHash(lo);
        let name: string = getQualifiedClassName(loFactory);

        if (!lo.isEssentialFilled) {
            throw new Error(name + ": " + ExternalLog.MESSAGE_NO_ESSENTIALS);
        }

        if (!force) {
            // check if event was already dispatched (i.e. hash is already in the queue)
            if (this._dispatchedHashes.indexOf(hash) >= 0) {
                logger.debug(this.parseMessage(ExternalLog.MESSAGE_SKIP, name, lo.subErrorId));
                return;
            }

            // check if queue reached max capacity
            if (this._dispatchedHashes.length >= ExternalLog.DISPATCHED_HASHES_MAX_LENGTH) {
                this.cleanOlderHashes();
            }

            // store hash of log event
            this._dispatchedHashes.push(hash);
        }

        logger.info(this.parseMessage(ExternalLog.MESSAGE_SEND, name, lo.subErrorId, JSON.stringify(lo.data)));

        this.send(lo);
    }

    private init(): void {
        this._isActive = true;
        this._dispatchedHashes = [];
        this._facade = new LODataFacade();
    }

    private takeHash(lo: AbstractLogObject): string {
        // to prevent dynamic field resolution
        let list: any[] = [];

        lo.logData.forEach((value: string, key: string) => {
            if (ExternalLog.EXCLUDED_PARAMS_FROM_HASH.indexOf(key) < 0) {
                list.push(key + value);
            }
        });

        list.sort();

        return new MD5().update(list.join("")).digest("hex");
    }

    private cleanOlderHashes(): void {
        while (this._dispatchedHashes.length >= ExternalLog.DISPATCHED_HASHES_MAX_LENGTH) {
            this._dispatchedHashes.shift();
        }
    }

    private send(lo: AbstractLogObject): void {
        let request: URLRequest = new URLRequest(
            ExternalLoggingConstants.LOG_API_URL,
            new URLSearchParams(lo.data).toString(),
            "POST"
        );
        let loader = new URLLoaderService(request);

        // send request to logging endpoint
        loader.load().then(response => {
            loader.dispose();
        }).catch(error => {
            loader.dispose();
            logger.warn(error);
        });
    }

    private parseMessage(message: string, ...args: any[]): string {
        let parsedMessage: string = message;

        for (let i: number = 0; i < args.length; i++) {
            parsedMessage = parsedMessage.replace(`{${i}}`, args[i]);
        }

        return parsedMessage;
    }

    public get isActive(): boolean {
        return this._isActive;
    }

    public set isActive(value: boolean) {
        this._isActive = value;
        logger.debug(this.parseMessage(ExternalLog.MESSAGE_STATUS_CHANGE, value));
    }
}
